import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import DateRangePicker from "../../../../components/DateRangePicker";
import { customStyles } from "../../../../assets/table-style";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import BASE_URL from "../../../../assets/global/url";
import swal from "sweetalert";
import { formatToTwoDecimal } from "../../../../utils/numberFormatter"

const FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Transaction ID", value: "transaction_id" },
  { label: "Destination", value: "destination" },
  { label: "Container Number", value: "container_number" },
  { label: "Pier", value: "pier" },
  { label: "DR Number", value: "dr_number" },
  { label: "PO Number", value: "po_number" },
  { label: "Payment Terms", value: "payment_terms" },
  { label: "Status", value: "status" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "n/a";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const tableStyles = {
  ...customStyles,
  rows: {
    ...customStyles?.rows,
    style: {
      ...(customStyles?.rows?.style ?? {}),
      cursor: "default",
    },
  },
};

const InvoiceTransactionList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedIds = location.state?.transactions ?? [];
  const customer = location.state?.customer ?? null;

  const [searchText, setSearchText] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const debounceTimer = useRef(null);
  const dropdownRef = useRef(null);

  const invoiceData = useServerPagination(
    selectedIds.length > 0 ? `${BASE_URL}/invoice/fetchReOpenInvoices` : null,
    10,
    {
      ids: JSON.stringify(selectedIds),
      searchText: "",
      filterColumn: "all",
      startDate: null,
      endDate: null,
    },
  );

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      invoiceData.updateParams({
        ids: JSON.stringify(selectedIds),
        searchText: value,
        filterColumn,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    }, 500);
  };

  const handleFilterSelect = (value) => {
    setFilterColumn(value);
    setDropdownOpen(false);
    invoiceData.updateParams({
      ids: JSON.stringify(selectedIds),
      searchText,
      filterColumn: value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange({ startDate, endDate });
    invoiceData.updateParams({
      ids: JSON.stringify(selectedIds),
      searchText,
      filterColumn,
      startDate,
      endDate,
    });
  };

  const handleEdit = (row) => {
    navigate(`/sales/transaction-invoice-update/${row.sales_invoice_id}`);
  };

  const columns = [
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_id || "--",
      cell: (row) => <div>{row.transaction_id || "--"}</div>,
      wrap: true,
      grow: 2,
    },
    {
      name: "Destination",
      selector: (row) => row.destination || "--",
      cell: (row) => <div>{row.destination || "--"}</div>,
      wrap: true,
      grow: 2,
    },
    {
      name: "Container Number",
      selector: (row) => row.container_number || "n/a",
      cell: (row) => <div>{row.container_number || "n/a"}</div>,
      wrap: true,
      grow: 2,
    },
    {
      name: "Pier",
      selector: (row) => row.pier || "n/a",
      cell: (row) => <div>{row.pier || "n/a"}</div>,
      wrap: true,
      grow: 1,
    },
    {
      name: "Customer",
      selector: (row) =>
        row.Customer?.first_name && row.Customer?.last_name
          ? `${row.Customer.first_name} ${row.Customer.last_name}`
          : row.Customer?.company_name || "--",
      wrap: true,
      grow: 2,
    },
    {
      name: "Invoice Date",
      selector: (row) => row.invoice_date,
      cell: (row) => <div>{formatDate(row.invoice_date)}</div>,
      wrap: true,
      grow: 1,
    },
    {
      name: "Date Approved",
      selector: (row) => row.date_approved,
      cell: (row) => <div>{formatDate(row.date_approved)}</div>,
      wrap: true,
      grow: 1,
    },
    {
      name: "DR Number",
      selector: (row) => row.dr_number || "n/a",
      cell: (row) => <div>{row.dr_number || "n/a"}</div>,
      wrap: true,
      grow: 1,
    },
    {
      name: "PO Number",
      selector: (row) => row.po_number || "n/a",
      cell: (row) => <div>{row.po_number || "n/a"}</div>,
      wrap: true,
      grow: 1,
    },
    {
      name: "Re-Open Amount",
      selector: (row) => row.re_open_amount,
      cell: (row) => {
        const amount = Number(row.re_open_amount);

        const textColor =
          row.re_open_amount == null
            ? "inherit"
            : amount < 0
            ? "#dc3545" 
            : "#198754"; 

        return (
          <div style={{ position: "relative", paddingTop: "14px", width: "100%" }}>
            <div style={{ fontWeight: 500, color: textColor }}>
              {row.re_open_amount != null
                ? formatToTwoDecimal(amount)
                : "--"}
            </div>
          </div>
        );
      },
      wrap: true,
      grow: 1,
    },
    {
      name: "Total Amount",
      selector: (row) => row.total_amount,
      cell: (row) => (
        <div
          style={{ position: "relative", paddingTop: "14px", width: "100%" }}
        >
          <span
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              fontSize: "9px",
              fontWeight: "700",
              padding: "1px 6px",
              borderRadius: "20px",
              whiteSpace: "nowrap",
              ...(row.vat_percent > 0
                ? {
                    backgroundColor: "#d4edda",
                    color: "#2D6A4F",
                    border: "1px solid #b7dfc5",
                  }
                : {
                    backgroundColor: "#e9ecef",
                    color: "#6c757d",
                    border: "1px solid #ced4da",
                  }),
            }}
          >
            {row.vat_percent > 0 ? `VAT (${row.vat_percent}%)` : "Non-VAT"}
          </span>

          {/* Amount */}
          <div style={{ fontWeight: 500 }}>
            {row.total_amount != null
              ? formatToTwoDecimal(row.total_amount)
              : "--"}
          </div>
        </div>
      ),
      wrap: true,
      grow: 1,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => {
        let color;
        switch (row.status) {
          case "Pending":
            color = "#FFA500";
            break;
          case "Approved":
            color = "#3B9F3F";
            break;
          case "Returned":
          case "Rejected":
            color = "#FF0000";
            break;
          default:
            color = "initial";
        }
        return (
          <div
            style={{
              padding: "5px 10px",
              borderRadius: "5px",
              color,
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
          >
            {row.status || "--"}
          </div>
        );
      },
      wrap: true,
      grow: 1,
    },
    {
      name: "Action",
      cell: (row) => {
        const isReOpened = Number(row.re_open_amount) > 0;

        const handleClick = () => {
          if (isReOpened) {
            swal({
              title: "Already Re-Opened",
              text: "This transaction has already been re-opened. Please go to the Sales Management Pending tab to view the re-open transaction.", 
              icon: "warning",
              button: "OK",
            });
          } else {
            handleEdit(row);
          }
        };

        return (
          <button
            title={isReOpened ? "Already Re-Opened" : "Edit"}
            onClick={handleClick}
            style={{
              background: "none",
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "5px 8px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#555",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#2D2C67";
              e.currentTarget.style.color = "#2D2C67";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#ccc";
              e.currentTarget.style.color = "#555";
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        );
      },
      ignoreRowClick: true,
      width: "80px",
      center: true,
    },
  ];

  // ── Derived ───────────────────────────────────────────────────────────────

  const activeFilterLabel =
    FILTER_OPTIONS.find((o) => o.value === filterColumn)?.label ?? "All";

  const customerDisplayName = customer
    ? customer.type === "company"
      ? customer.company_name || "--"
      : `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim() ||
        "--"
    : "--";

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-2">
          <Link to="/sales/invoices" className="text-dark">
            <i className="fa-solid fa-arrow-left fs-5"></i>
          </Link>

          <div>
            <h4 className="mb-0 fw-bold">Invoice Transaction List</h4>
            {customer && (
              <small className="text-muted">
                Customer:{" "}
                <span className="text-primary fw-semibold">
                  {customerDisplayName}
                </span>{" "}
                · {selectedIds.length} record
                {selectedIds.length !== 1 ? "s" : ""} selected
              </small>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-end mb-3 gap-2">
        <div style={{ minWidth: "250px" }}>
          <DateRangePicker
            label="Invoice Date"
            onDateRangeChange={handleDateRangeChange}
          />
        </div>

        <div
          className="input-group"
          ref={dropdownRef}
          style={{ position: "relative", maxWidth: "400px" }}
        >
          <input
            type="text"
            className="form-control"
            placeholder={`Search by ${activeFilterLabel}...`}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setDropdownOpen((prev) => !prev)}
            title="Filter by column"
          >
            <i className="fa-solid fa-sliders"></i>
          </button>

          {dropdownOpen && (
            <ul
              className="dropdown-menu show"
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                zIndex: 1055,
                minWidth: "200px",
              }}
            >
              {FILTER_OPTIONS.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    className={`dropdown-item ${filterColumn === opt.value ? "active" : ""}`}
                    onClick={() => handleFilterSelect(opt.value)}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selectedIds.length === 0 && (
        <div className="alert alert-warning">
          No invoice records were selected. Please go back and select at least
          one.
        </div>
      )}

      <DataTable
        columns={columns}
        data={invoiceData.data ?? []}
        customStyles={tableStyles}
        highlightOnHover
        responsive
        progressPending={invoiceData.loading}
        progressComponent={
          <div className="py-4 text-muted">Loading records...</div>
        }
        noDataComponent={
          <div className="py-4 text-muted">
            No matching invoice records found.
          </div>
        }
      />

      <div className="d-flex justify-content-end mt-2">
        <PaginationControls {...invoiceData} />
      </div>
    </div>
  );
};

export default InvoiceTransactionList;
